import * as React from "react";
import Progress, { type ProgressSize } from "@imminently/decisively-ui/Progress";
import Select from "@imminently/decisively-ui/Select";
import TextInput from "@imminently/decisively-ui/TextInput";
import {
  ComponentPage,
  ComponentPreview,
  ComponentSection,
  ControlPanel,
  ControlRow,
} from "../components/ComponentPage";

export function ProgressPage() {
  const [value, setValue] = React.useState(64);
  const [size, setSize] = React.useState<ProgressSize>("md");

  return (
    <ComponentPage
      title="Progress"
      description="Displays progress for long-running tasks."
    >
      <ComponentSection title="Playground" description="Adjust the value and size.">
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <div className="w-full max-w-md space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Load test</span>
                <span className="text-muted-foreground">{value}%</span>
              </div>
              <Progress value={value} size={size} />
            </div>
          </ComponentPreview>
          <ControlPanel>
            <ControlRow label="Value">
              <TextInput
                type="number"
                min={0}
                max={100}
                value={String(value)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setValue(Math.min(100, Math.max(0, Number(event.target.value) || 0)))}
              />
            </ControlRow>
            <ControlRow label="Size">
              <Select
                value={size}
                onValueChange={(nextSize) => setSize((nextSize ?? "md") as ProgressSize)}
                options={[
                  { value: "sm", label: "Small" },
                  { value: "md", label: "Medium" },
                  { value: "lg", label: "Large" },
                ]}
              />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      <ComponentSection title="Sizes">
        <ComponentPreview className="flex-col items-stretch gap-6">
          <Progress value={35} size="sm" />
          <Progress value={55} size="md" />
          <Progress value={75} size="lg" />
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection title="States">
        <ComponentPreview className="flex-col items-stretch gap-6">
          {[
            { label: "Not started", value: 0 },
            { label: "Running", value: 50 },
            { label: "Complete", value: 100 },
            { label: "Intermediate", value: null },
          ].map((state) => (
            <div key={state.label} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{state.label}</span>
                <span className="text-muted-foreground">{state.value === null ? "Loading" : `${state.value}%`}</span>
              </div>
              <Progress value={state.value} />
            </div>
          ))}
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection title="Theme Colors">
        <ComponentPreview className="flex-col items-stretch gap-6">
          {[
            { label: "Primary", intent: "primary", value: 45 },
            { label: "Secondary", intent: "secondary", value: 55 },
            { label: "Success", intent: "success", value: 70 },
            { label: "Warning", intent: "warning", value: 60 },
            { label: "Destructive", intent: "destructive", value: 35 },
          ].map((state) => (
            <div key={state.label} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{state.label}</span>
                <span className="text-muted-foreground">{state.value}%</span>
              </div>
              <Progress value={state.value} intent={state.intent as any} />
            </div>
          ))}
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
